import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {Contact} from "../model/contact";
import {BehaviorSubject, catchError, map, Observable, of} from "rxjs";
import {Group} from "../model/group";
import {Rule} from "../model/rule";

@Injectable({
  providedIn: 'root'
})
export class RulesService {
  private readonly _contacts: BehaviorSubject<Array<Contact>>;
  private readonly _groups: BehaviorSubject<Array<Group>>;

  constructor(private readonly httpClient: HttpClient) {
    this._contacts = new BehaviorSubject<Array<Contact>>([]);
    this._groups = new BehaviorSubject<Array<Group>>([]);
  }

  get contacts(): Observable<Array<Contact>> {
    return this._contacts.asObservable();
  }

  get groups(): Observable<Array<Group>> {
    return this._groups.asObservable();
  }

  get contactsValue(): Array<Contact> {
    return this._contacts.value;
  }

  get groupsValue(): Array<Group> {
    return this._groups.value;
  }

  loadRules(): void {
    this.loadContacts();
    this.loadGroups();
  }

  clearRules(): void {
    this._contacts.next([]);
    this._groups.next([]);
  }

  loadContacts(): void {
    this.httpClient.get<Array<Contact>>('/rbt/rules/contacts')
      .pipe(catchError(() => of(Array<Contact>())))
      .subscribe(contacts => this._contacts.next(contacts));
  }

  loadGroups(): void {
    this.httpClient.get<Array<Group>>('/rbt/rules/groups')
      .pipe(catchError(() => of(Array<Group>())))
      .subscribe(groups => this._groups.next(groups));
  }

  createContact(contact: Contact): Observable<number> {
    return this.httpClient.post<number>('/rbt/rules/contacts', contact);
  }

  editContact(contact: Contact): Observable<number> {
    return this.httpClient.put<void>('/rbt/rules/contacts', contact, {
      observe: 'response'
    })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      )
  }

  updateContactRules(contactId: number, rules: Array<Rule>): Observable<number> {
    return this.httpClient.put<void>(`/rbt/rules/contacts/${contactId}/rules`, rules, {
      observe: 'response'
    })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      )
  }

  deleteContactRules(contactId: number): Observable<number> {
    return this.httpClient.delete<void>(`/rbt/rules/contacts/${contactId}/rules`, {
      observe: 'response'
    }).pipe(
      map(response => response.status),
      catchError((error: HttpErrorResponse) => of(error.status))
    )
  }

  deleteContact(contactId: number): Observable<number> {
    return this.httpClient.delete<void>(`/rbt/rules/contacts/${contactId}`, {
      observe: 'response'
    })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      )
  }

  createGroup(group: Group): Observable<number> {
    return this.httpClient.post<number>('/rbt/rules/groups', group);
  }

  editGroup(group: Group): Observable<number> {
    return this.httpClient.put<void>('/rbt/rules/groups', group, {
      observe: 'response'
    })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      )
  }

  updateGroupRules(groupId: number, rules: Array<Rule>): Observable<number> {
    return this.httpClient.put<void>(`/rbt/rules/groups/${groupId}/rules`, rules, {
      observe: 'response'
    })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      )
  }

  deleteGroupRules(groupId: number): Observable<number> {
    return this.httpClient.delete<void>(`/rbt/rules/groups/${groupId}/rules`, {
      observe: 'response'
    })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      )
  }

  deleteGroup(groupId: number): Observable<number> {
    return this.httpClient.delete<void>(`/rbt/rules/groups/${groupId}`, {
      observe: 'response'
    })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      )
  }
}
